import { useEffect, useState } from "react";
import { ArrowsLeftRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import FormDialog from "./FormDialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import SearchableSelect from "./SearchableSelect";

const ROLE_LABELS = {
    super_admin: "Super Admin",
    accounts: "Accounts",
    sales: "Sales",
    viewer: "Viewer",
};

/**
 * Super-admin-only dialog to reassign ownership of a record.
 *
 * Props:
 *   open / onOpenChange         — dialog state
 *   entityLabel                  — "Client" | "PO" | "Proposal" | "Lead"
 *   entityName                   — display name (e.g. "Acme Industries")
 *   currentOwner                 — { id, name, email, role } | null
 *   reassignUrl                  — relative API URL (e.g. `/billing/client/CL-123/owner`)
 *   cascadePreviewUrl            — optional; when set, fetches counts and shows
 *                                  an opt-in cascade checkbox. Iter 25 — only
 *                                  Client reassignment uses this.
 *   onSuccess(updatedRecord)     — called after successful PATCH; parent refreshes
 *   testIdPrefix                 — default "reassign-owner"
 */
export const ReassignOwnerDialog = ({
    open, onOpenChange,
    entityLabel = "Record",
    entityName = "",
    currentOwner = null,
    reassignUrl,
    cascadePreviewUrl = null,
    onSuccess,
    testIdPrefix = "reassign-owner",
}) => {
    const [users, setUsers] = useState([]);
    const [picked, setPicked] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cascade, setCascade] = useState(false);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!open) {
            setPicked("");
            setCascade(false);
            setPreview(null);
            return;
        }
        setLoading(true);
        api.get("/users")
            .then(({ data }) => {
                const active = (data.items || []).filter((u) => u.is_active !== false);
                setUsers(active);
            })
            .catch((err) => toast.error(formatApiError(err, "Failed to load users")))
            .finally(() => setLoading(false));
        // Fetch cascade preview when supported (Client reassign only)
        if (cascadePreviewUrl) {
            api.get(cascadePreviewUrl)
                .then(({ data }) => setPreview(data))
                .catch(() => setPreview(null));
        }
    }, [open, cascadePreviewUrl]);

    const submit = async () => {
        if (!picked) {
            toast.error("Pick a user first");
            return;
        }
        if (currentOwner && picked === currentOwner.id) {
            toast.info("That's already the current owner");
            return;
        }
        setSubmitting(true);
        try {
            const body = { user_id: picked };
            if (cascadePreviewUrl) body.cascade = cascade;
            const { data } = await api.patch(reassignUrl, body);
            const newOwnerName = users.find((u) => u.id === picked)?.name || "user";
            const cs = data?._cascade_summary;
            const cascadeNote = cs && Object.values(cs).some((v) => v > 0)
                ? ` · cascaded ${Object.entries(cs).filter(([_, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(" · ")}`
                : "";
            toast.success(`Ownership transferred to ${newOwnerName}${cascadeNote}`);
            onOpenChange(false);
            onSuccess && onSuccess(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to reassign owner"));
        } finally {
            setSubmitting(false);
        }
    };

    const options = users.map((u) => ({
        value: u.id,
        label: u.name || u.email,
        hint: `${u.email} · ${ROLE_LABELS[u.role] || u.role}`,
    }));

    // Treat user-picked changes as "dirty" — losing a 30s search-and-pick
    // is annoying, even if it's only one field. Cascade toggle is intentional
    // user input too.
    const isDirty = Boolean(picked) || cascade;

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            isDirty={isDirty}
            contentClassName="max-w-md"
            testId={`${testIdPrefix}-dialog`}
            header={
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ArrowsLeftRight size={16} weight="bold" /> Reassign owner
                    </DialogTitle>
                    <DialogDescription>
                        Transfer ownership of <span className="font-semibold text-slate-900">{entityLabel}{entityName ? ` · ${entityName}` : ""}</span> to another user.
                        The change is audit-logged. Useful when reps leave or pipelines are redistributed.
                    </DialogDescription>
                </DialogHeader>
            }
        >
                <div className="space-y-3 py-2">
                    <div className="text-xs text-slate-500">
                        <span className="reedos-eyebrow text-[9px]">Current owner</span>
                        <div className="mt-1 text-slate-900 font-medium" data-testid={`${testIdPrefix}-current-owner`}>
                            {currentOwner ? (
                                <>
                                    {currentOwner.name || currentOwner.email}
                                    <span className="text-slate-500 font-normal text-xs ml-1.5">
                                        ({ROLE_LABELS[currentOwner.role] || currentOwner.role})
                                    </span>
                                </>
                            ) : (
                                <span className="text-slate-400">Unassigned</span>
                            )}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">New owner</Label>
                        <SearchableSelect
                            options={options}
                            value={picked}
                            onChange={setPicked}
                            placeholder={loading ? "Loading users…" : "— Select user —"}
                            searchPlaceholder="Type to filter by name, email or role…"
                            emptyText="No matching user."
                            testId={`${testIdPrefix}-user`}
                            className="mt-1.5"
                            disabled={loading || submitting}
                        />
                    </div>
                    {cascadePreviewUrl && preview && (
                        <div
                            className="rounded-md border border-amber-200 bg-amber-50/40 px-3 py-2.5 mt-2"
                            data-testid={`${testIdPrefix}-cascade-block`}
                        >
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="mt-0.5 accent-amber-600"
                                    checked={cascade}
                                    onChange={(e) => setCascade(e.target.checked)}
                                    disabled={submitting || (preview.leads + preview.proposals + preview.pos) === 0}
                                    data-testid={`${testIdPrefix}-cascade-toggle`}
                                />
                                <div className="text-xs leading-relaxed">
                                    <div className="font-semibold text-slate-900">
                                        Also reassign linked entities owned by current owner
                                    </div>
                                    <div className="text-slate-600 mt-0.5" data-testid={`${testIdPrefix}-cascade-counts`}>
                                        {preview.leads + preview.proposals + preview.pos === 0 ? (
                                            <span className="italic">No related leads / proposals / POs owned by the current owner.</span>
                                        ) : (
                                            <>
                                                <span className="font-mono">{preview.leads}</span> lead{preview.leads === 1 ? "" : "s"}
                                                {" · "}
                                                <span className="font-mono">{preview.proposals}</span> proposal{preview.proposals === 1 ? "" : "s"}
                                                {" · "}
                                                <span className="font-mono">{preview.pos}</span> PO{preview.pos === 1 ? "" : "s"}
                                            </>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-slate-500 mt-1.5">
                                        Invoices, payments &amp; credit notes are <strong>never</strong> cascaded — those stay with Accounts.
                                    </div>
                                </div>
                            </label>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="ghost"
                        type="button"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        data-testid={`${testIdPrefix}-cancel`}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={!picked || submitting || (currentOwner && picked === currentOwner.id)}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid={`${testIdPrefix}-submit`}
                    >
                        {submitting ? "Reassigning…" : "Reassign"}
                    </Button>
                </DialogFooter>
        </FormDialog>
    );
};

export default ReassignOwnerDialog;
